import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { Testimonial } from '@/types/database';

export function useTestimonials(options?: { includeUnpublished?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.testimonials.list(), options],
    queryFn: async () => {
      let query = rest.from<Testimonial>('testimonials')
        .select('*')
        .order('order_index', { ascending: true });

      if (!options?.includeUnpublished) {
        query = query.eq('status', 'published');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Testimonial[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeaturedTestimonials() {
  return useQuery({
    queryKey: queryKeys.testimonials.featured(),
    queryFn: async () => {
      const { data, error } = await rest.from<Testimonial>('testimonials')
        .select('*')
        .eq('status', 'published')
        .eq('is_featured', true)
        .order('order_index', { ascending: true })
        .limit(6);

      if (error) throw error;
      return data as Testimonial[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (testimonial: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('testimonials')
        .insert(testimonial as any)
        .select()
        .single();

      if (error) throw error;
      return data as Testimonial;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.testimonials.all });
    },
  });
}

export function useUpdateTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Testimonial> & { id: string }) => {
      const { data, error } = await supabase
        .from('testimonials')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Testimonial;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.testimonials.all });
    },
  });
}

export function useDeleteTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.testimonials.all });
    },
  });
}
